import React, {useState} from 'react';
import _ from 'lodash';
import {useQueryClient} from '@tanstack/react-query';
import {Stack, Typography, Link} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {useBusyCallback, useHotkey} from '../util/react_hooks.js';
import api from '../util/api.js';
import {openModal, closeModal} from './Modals.js';
import {openSignupModal} from './Signup.js';
import SCButton from './SCButton.js';
import SCTextField from './SCTextField.js';
import storage from '../util/storage.js';
import {useUser} from '../util/user.js';
import {useLoaderState} from './Loader.js';

const PasswordResetModal = ()=>{
    let [email, setEmail] = useState('');
    let [error, setError] = useState();
    let [onConfirm, confirming] = useBusyCallback(async ()=>{
        setError();
        try {
            await api.passwordResetRequest(email);
            openModal({
                header: 'איפוס סיסמא',
                content: <Stack gap={2} direction="column">
                  <Stack gap={2} direction="row" alignItems="center">
                    <CheckCircleIcon sx={{color: 'status.success'}}/>
                    <Typography>נשלח מייל אימות לכתובת <strong>{email}</strong></Typography>
                  </Stack>
                  <Typography>להשלמת איפוס הסיסמא, לחצו על הלינק שקיבלתם במייל</Typography>
                </Stack>,
            });
        } catch (e) {
            if (e.code=='validation_error')
                return void setError(<>{e.extra.map(c=>c.message).filter(Boolean).join(<br/>)}</>);
            if (e.code=='user_not_found')
                return void setError('לא קיים משתמש עם המייל הזה');
            setError('אירעה שגיאה, נסו שוב מאוחר יותר');
        }
    }, [email]);
    useHotkey('Enter', onConfirm);
    return <Stack direction="column" gap={2} justifyContent="center" alignItems="center">
      <SCTextField required label={`דוא"ל`} variant="outlined" value={email} onChange={setEmail}/>
      <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" width="100%">
        <SCButton text="איפוס" variant="contained" onClick={onConfirm} loading={confirming} size="large"/>
        <Link onClick={openLoginModal}>חזרה להתחברות</Link>
      </Stack>
      {error&&<Typography color="red">{error}</Typography>}
    </Stack>;
};

export const LoginForm = props=>{
    let {onDone, note=null} = props;
    let [email, setEmail] = useState('');
    let [password, setPassword] = useState('');
    let [error, setError] = useState();
    let queryClient = useQueryClient();
    let [onConfirm, confirming] = useBusyCallback(async ()=>{
        setError();
        try {
            let {user, token, refreshToken} = await api.login({email, password});
            storage.set('sc_user', user.id);
            storage.set('sc_token', token);
            storage.set('sc_refresh_token', refreshToken);
            queryClient.setQueryData(['user'], user);
            queryClient.invalidateQueries({queryKey: ['user']});
            onDone?.();
        } catch (e) {
            if (e.code=='unauthorized')
                return void setError('הדוא"ל או הסיסמה שגויים');
            if (e.code=='email_not_verified')
                return void setError(e.message);
            if (e.code=='validation_error')
                return void setError(<>{e.extra.map(c=>c.message).filter(Boolean).join(<br/>)}</>);
            setError('אירעה שגיאה, נסו שוב מאוחר יותר');
        }
    }, [email, password, queryClient, onDone]);
    useHotkey('Enter', onConfirm);
    return <Stack direction="column" gap={2} justifyContent="center"
      alignItems="center">
      {note}
      <SCTextField required label={`דוא"ל`} variant="outlined" value={email} onChange={setEmail}/>
      <SCTextField required label="סיסמא" type="password" variant="outlined" value={password} onChange={setPassword}/>
      <Stack direction="column" alignItems="center" justifyContent="center" width="100%" gap={2}>
        <SCButton text="התחברות" variant="contained" onClick={onConfirm} loading={confirming} size="large"/>
        <Link onClick={openSignupModal}>עוד אין לך חשבון? לחצ.י כאן להרשמה</Link>
        <Link onClick={()=>openModal({
            header: 'איפוס סיסמא',
            content: <PasswordResetModal/>,
        })}>שכחת סיסמא?</Link>
      </Stack>
      {error&&<Typography color="red">{error}</Typography>}
    </Stack>;
};

export const openLoginModal = ({onDone, onClose, note})=>openModal({
    header: 'התחברות',
    onClose,
    content: <LoginForm note={note} onDone={()=>{
        closeModal();
        onDone?.();
    }}/>,
});

export const withLoginRequire = Comp=>props=>{
    let [user, userLoading] = useUser();
    useLoaderState(userLoading);
    if (userLoading)
        return null;
    if (user)
        return <Comp {...props}/>;
    return <Stack justifyContent="center" alignItems="center" margin={5}>
      <Stack direction="column" gap={2} justifyContent="center" alignItems="center">
        <Typography variant="h3">התחברות</Typography>
        <LoginForm/>
      </Stack>
    </Stack>;
};
